import useLeagueSelection from "../useLeagueSelection";
import { HQ } from "../sportConfig";
import ShareButton from "../ShareButton";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useFavoriteTeams } from "../FavoriteTeams";
import "./UpcomingMatches.css";

const dateLabel = (date) => new Intl.DateTimeFormat("sv-SE", {
  weekday: "long", day: "numeric", month: "long",
}).format(new Date(`${date}T12:00:00`));

export default function UpcomingMatches() {
  const [matches, setMatches] = useState(null);
  const [error, setError] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { teamIds, ready, authenticated, notice } = useFavoriteTeams();
  const [visible, setVisible] = useState(30);
  const [leagueMenuOpen, setLeagueMenuOpen] = useState(false);
  const [selectedLeagues, saveLeagues] = useLeagueSelection(searchParams, "upcoming");
  const favoritesPending = matches?.length > 0 && matches.some((match) => !match.home_team_id || !match.away_team_id);
  const mineOnly = searchParams.get("mine") === "1" && !favoritesPending;

  const updateParams = (updates) => {
    const next = new URLSearchParams(searchParams);
    if (Object.prototype.hasOwnProperty.call(updates, "league")) saveLeagues(updates.league ? [updates.league] : []);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    setSearchParams(next);
    setVisible(30);
  };

  const toggleLeague = (league) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("league");
    const nextLeagues = selectedLeagues.includes(league)
      ? selectedLeagues.filter((item) => item !== league)
      : [...selectedLeagues, league];
    saveLeagues(nextLeagues);
    nextLeagues.forEach((item) => nextParams.append("league", item));
    setSearchParams(nextParams);
    setVisible(30);
  };

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${HQ.dataApi}/upcoming`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error("Could not load fixtures");
        return response.json();
      })
      .then((data) => setMatches(data.matches || []))
      .catch((failure) => { if (failure.name !== "AbortError") setError(true); });
    return () => controller.abort();
  }, []);

  const leagues = useMemo(() => [...new Set((matches || []).map((match) => match.league))].sort((a, b) => a.localeCompare(b, "sv")), [matches]);
  const filtered = (matches || []).filter((match) =>
    (!selectedLeagues.length || selectedLeagues.includes(match.league))
    && (!mineOnly || teamIds.includes(match.home_team_id) || teamIds.includes(match.away_team_id))
  );
  const shown = filtered.slice(0, visible);
  const returnTo = `/upcoming${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;

  return <main className="upcoming-page">
    <div className="upcoming-page__top hq-page-actions"><Link to="/">← Matcher dag för dag</Link><Link to="/series?vy=spelschema">Serier →</Link><ShareButton path="/upcoming" title="Kommande innebandymatcher" /></div>
    <header className="upcoming-page__header">
      <p className="upcoming-page__eyebrow">Sportssims serier</p>
      <h1>Spelschema</h1>
      <p>Alla kommande matcher till säsongsslut. Välj serie eller visa bara dina lag.</p>
    </header>
    {error && <p className="error-message" role="alert">Det gick inte att hämta spelschemat. Ladda om sidan och försök igen.</p>}
    {!error && !matches && <p className="upcoming-page__message" role="status">Hämtar spelschema…</p>}
    {matches && <>
      <div className="match-controls upcoming-page__controls">
        <div className="match-filter">
          <span className="match-filter__label">Serier</span>
          <div className="league-dropdown">
            <button type="button" className="league-dropdown__trigger" aria-expanded={leagueMenuOpen}
              aria-controls="upcoming-league-options" onClick={() => setLeagueMenuOpen((open) => !open)}>
              <span>{selectedLeagues.length ? `${selectedLeagues.length} serier valda` : `Alla serier (${matches.length})`}</span>
              <span aria-hidden="true">▾</span>
            </button>
            {selectedLeagues.length > 0 && <button type="button" className="league-dropdown__clear"
              onClick={() => updateParams({ league: "" })} aria-label="Rensa serieval">Rensa</button>}
            {leagueMenuOpen && <div id="upcoming-league-options" className="league-options" role="group" aria-label="Filtrera på serier">
              {leagues.map((name) => <label key={name} className="league-option">
                <input type="checkbox" checked={selectedLeagues.includes(name)} onChange={() => toggleLeague(name)} />
                <span>{name}</span>
              </label>)}
            </div>}
          </div>
        </div>
        <button type="button" className={`favorite-filter${mineOnly ? " favorite-filter--active" : ""}`}
          aria-pressed={mineOnly} disabled={!ready || favoritesPending} onClick={() => updateParams({ mine: mineOnly ? "" : "1" })}>
          <span aria-hidden="true">★</span> Mina lag
        </button>
      </div>
      {favoritesPending && <p className="upcoming-page__availability" role="status">Favoritfiltret blir tillgängligt när spelschemat uppdaterats.</p>}
      {notice && <p className="favorites-notice" role="status">{notice}</p>}
      {filtered.length === 0 && <div className="no-filtered-matches">
        <p>{mineOnly && !teamIds.length ? "Du har inga favoritlag ännu. Öppna en match och tryck på stjärnan vid ett lag." : mineOnly ? "Dina lag har inga kommande matcher i de valda serierna." : "Inga kommande matcher i de valda serierna."}</p>
        {mineOnly && !authenticated && <small>Mina lag sparas på den här enheten.</small>}
        <button type="button" onClick={() => updateParams({ league: "", mine: "" })}>Visa alla matcher</button>
      </div>}
      <div className="match-list match-list--compact upcoming-page__list">
        {shown.map((match, index) => <React.Fragment key={match.id}>
          {(index === 0 || shown[index - 1].date !== match.date) && <h2>{dateLabel(match.date)}</h2>}
          <Link className="match-card" to={`/match?url=${encodeURIComponent(match.match_link + "/laguppstallning")}&returnTo=${encodeURIComponent(returnTo)}`} state={{ match }}>
            <div className="league-info">{match.league}</div>
            <div className="match-details">
              <div className="team"><span className="team-name">{match.home_team}</span></div>
              <div className="match-time">{match.time}</div>
              <div className="team"><span className="team-name">{match.away_team}</span></div>
            </div>
          </Link>
        </React.Fragment>)}
      </div>
      {visible < filtered.length && <button className="upcoming-page__more" type="button" onClick={() => setVisible(visible + 30)}>Visa fler matcher</button>}
    </>}
  </main>;
}
